import {
  Box,
  Text,
  Button,
  Image,
  Flex,
  Avatar,
  useBreakpointValue,
  Badge,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import {
  BikeIcon,
  BusIcon,
  CarIcon,
  LucideBike,
  TruckIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fetchUsersFromFirestore } from "../../../utils/firebaseHelpers";
import ArrowImg from "../../../assets/icons/arrow.png";
import { useBusinessInfo } from "../../Hooks/useBusinessInfo";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";

const cardsBorderRadii ="8px";

interface TripProps {
  trip: {
    id: string;
    from: { address: string; lat: number; lng: number };
    to: { address: string; lat: number; lng: number };
    date: string;
    seats: number;
    driverId: string;
    carType: string;
    price: number;
    departTime?: string;
    arriveTime?: string;
  };
}

interface FirestoreUser {
  id: string;
  name?: string;
  email?: string;
  photoURL?: string;
  role?: string;
}

export const getDriverInitials = (name?: string) => {
  if (!name) return "DR";
  const trimmed = name.trim();
  return (
    trimmed.charAt(0).toUpperCase() +
    trimmed.charAt(trimmed.length - 1).toUpperCase()
  );
};

export const HikeTripCard: React.FC<TripProps> = ({ trip }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const navigate = useNavigate();

  const [driver, setDriver] = useState<FirestoreUser | null>(null);
  const { businessInfo } = useBusinessInfo();
  const { theme } = useCustomTheme();

  const isDark = theme.name === "dark";
  const brand = theme.brand;

  // ✅ Centralized theme-driven styling
  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const textColor = theme.text;
  const mutedText = theme.textMuted;
  const subtleText = theme.textSubtle;
  const borderColor = theme.border;
  const borderStrong = theme.borderStrong;

  const cardShadow = theme.shadowSm;
  const hoverShadow = theme.shadowMd;

  const canBook = trip.seats > 0;

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await fetchUsersFromFirestore();
        const found = users.find((u: any) => u.id === trip.driverId);
        // Keeping your functionality intact (you had this intentionally disabled)
        // if (found && found?.name && found?.email) {
        //   setDriver({
        //     id: found.id,
        //     name: found.name,
        //     email: found.email,
        //     photoURL: found.photoURL || '',
        //     role: found.role || '',
        //   });
        // }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    loadUsers();
  }, [trip.driverId]);

  const handleBooking = () => {
    navigate(`/hike/checkout/${trip?.id}`);
  };

  const formatTime = (timeStr?: string) => {
    if (!timeStr) return "";
    const [h, m] = timeStr.split(":").map(Number);
    const date = new Date();
    date.setHours(h, m);
    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCarTypeIcon = (carType: string) => {
    const iconSize = 18;
    const color = brand.indigo || theme.secondary || "#6f7cff";

    switch (carType?.toLowerCase()) {
      case "bus":
        return <BusIcon size={iconSize} color={color} />;
      case "car":
        return <CarIcon size={iconSize} color={color} />;
      case "motorbike":
      case "bike":
        return <BikeIcon size={iconSize} color={color} />;
      case "truck":
      case "pickup":
      case "pickup truck":
        return <TruckIcon size={iconSize} color={color} />;
      case "motorcycle":
        return <LucideBike size={iconSize} color={color} />;
      default:
        return <CarIcon size={iconSize} color={color} />;
    }
  };

  useEffect(() => {
    if (businessInfo) {
      console.log(businessInfo);
    }
  }, [businessInfo.length]);

  return (
    <Flex
      borderRadius={cardsBorderRadii}
      px={{ base: 2, md: 5 }}
      py={{ base: 2, md: 3 }}
      bg={cardBg}
      boxShadow={cardShadow}
      border={`1px solid ${borderColor}`}
      overflow="hidden"
      justifyContent="space-between"
      w="100%"
      gap={4}
      flexDirection={{ base: "column", md: "row" }}
      color={textColor}
      transition="all 0.2s ease"
      _hover={{
        transform: "translateY(-2px)",
        boxShadow: hoverShadow,
      }}
    >
      {/* Business / Vehicle info */}
      <Flex alignItems="center">
        {businessInfo && (
          <Flex
            minW={{ base: "100%", md: "170px" }}
            display={{ base: "none", md: "flex" }}
            pr={2}
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            bg={isDark ? "rgba(255,255,255,0.02)" : softBg}
            borderRadius={cardsBorderRadii}
            px={3}
            py={3}
          >
            <Flex gap={3} alignItems="center">
              {businessInfo?.logoURL ? (
                <Avatar
                  size="sm"
                  src={businessInfo?.logoURL}
                  name={businessInfo.name}
                />
              ) : (
                <Avatar
                  size="sm"
                  name={businessInfo?.name}
                  bg={theme.primarySoft}
                  color={theme.primary}
                >
                  {getDriverInitials(businessInfo?.name)}
                </Avatar>
              )}

              <Box
                mt={1}
                fontSize="sm"
                textAlign="left"
                color={mutedText}
                maxW="110px"
              >
                <Text noOfLines={2} fontWeight="600" color={textColor}>
                  {businessInfo?.businessName || ""}
                </Text>
              </Box>
            </Flex>

            <Flex gap={2} mt={3} alignItems="center">
              {getCarTypeIcon(trip.carType)}
              <Text fontSize="sm" color={mutedText} fontWeight="500">
                {trip.carType}
              </Text>
            </Flex>
          </Flex>
        )}
      </Flex>

      {/* Route / time section */}
      <Flex
        alignItems="center"
        w="100%"
        justifyContent="space-between"
        minW={0}
        gap={2}
      >
        <Flex
          px={2}
          minW={0}
          whiteSpace="nowrap"
          borderLeft={{ base: "none", md: `1px solid ${borderColor}` }}
          pl={{ base: 0, md: 4 }}
          flexDirection="column"
        >
          <Box
            color={brand.electricBlue}
            fontWeight="700"
            fontSize={{ base: "16px", md: "20px" }}
          >
            {formatTime(trip.departTime)}
          </Box>
          <Box
            fontWeight="600"
            color={textColor}
            noOfLines={1}
            maxW={{ base: "120px", md: "220px" }}
          >
            {trip.from.address}
          </Box>
        </Flex>

        <Image
          w={{ base: "28px", md: "40px" }}
          src={ArrowImg}
          alt="Arrow Right"
          opacity={isDark ? 0.75 : 0.9}
          filter={isDark ? "brightness(0.95)" : "none"}
        />

        <Flex
          px={2}
          minW={0}
          whiteSpace="nowrap"
          pl={{ base: 0, md: 4 }}
          flexDirection="column"
        >
          <Box
            color={brand.indigo}
            fontWeight="700"
            fontSize={{ base: "16px", md: "20px" }}
          >
            {formatTime(trip.arriveTime)}
          </Box>
          <Box
            fontWeight="600"
            color={textColor}
            noOfLines={1}
            maxW={{ base: "120px", md: "220px" }}
          >
            {trip.to.address}
          </Box>
        </Flex>
      </Flex>

      {/* Stats + CTA */}
      <Flex
        alignItems="center"
        justifyContent="space-between"
        gap={{ base: 3, md: 2 }}
        flexWrap={{ base: "wrap", md: "nowrap" }}
      >
        <Flex
          px={2}
          borderLeft={{ base: "none", md: `1px solid ${borderColor}` }}
          pl={{ base: 0, md: 4 }}
          flexDirection="column"
          minW="88px"
        >
          <Box
            fontWeight="500"
            whiteSpace="nowrap"
            color={mutedText}
            fontSize="sm"
          >
            Seats Left
          </Box>
          <Text
            fontWeight="bold"
            color={
              trip.seats > 3
                ? theme.success
                : trip.seats > 0
                  ? theme.warning
                  : theme.danger
            }
          >
            {trip.seats}
          </Text>
        </Flex>

        <Flex
          px={2}
          borderLeft={{ base: "none", md: `1px solid ${borderColor}` }}
          pl={{ base: 0, md: 4 }}
          flexDirection="column"
          minW="100px"
        >
          <Box
            fontWeight="500"
            whiteSpace="nowrap"
            color={mutedText}
            fontSize="sm"
          >
            Price (MK)
          </Box>
          <Text fontWeight="bold" fontSize="md" color={textColor}>
            {trip.price.toLocaleString()}
          </Text>
        </Flex>

        <Flex
          alignItems="center"
          gap={2}
          borderLeft={{ base: "none", md: `1px solid ${borderColor}` }}
          pr={2}
          pl={{ base: 0, md: 4 }}
          h="100%"
        >
          <Button
            onClick={handleBooking}
            isDisabled={!canBook}
            size="md"
            minW="94px"
            borderRadius="5px"
            fontWeight="700"
            color={canBook ? theme.textOnPrimary : textColor}
            bgImage={canBook ? theme.gradients.primaryButton : undefined}
            bg={
              canBook
                ? undefined
                : isDark
                  ? "rgba(255,255,255,0.06)"
                  : "#edf1f7"
            }
            boxShadow={canBook ? theme.shadowSm : "none"}
            _hover={
              canBook
                ? {
                    bgImage: theme.gradients.primaryButtonHover,
                    transform: "translateY(-1px)",
                    boxShadow: theme.shadowMd,
                  }
                : {}
            }
            _active={canBook ? { transform: "translateY(0)" } : {}}
            _disabled={{
              opacity: 1,
              cursor: "not-allowed",
              color: isDark ? theme.textMuted : "#667085",
              bg: isDark ? "rgba(255,255,255,0.06)" : "#edf1f7",
            }}
            transition="all 0.2s ease"
          >
            {canBook ? "Book" : "Full"}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
